import React, { useState, useMemo } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { type ColumnDef } from '@tanstack/react-table';
import DataTable from '../../components/reusables/DataTable';

type Keyword = {
    id: number;
    keyword: string;
    occurrences: number;
};

interface KeywordAnalysisModalProps {
    visible: boolean;
    onClose: () => void;
    onCreateRule: (selectedKeyword: Keyword) => void;
    onResolve: () => void;
}

const keywordsData: Keyword[] = [
    { id: 1, keyword: 'Dell', occurrences: 56 },
    { id: 2, keyword: 'Corporate', occurrences: 45 },
    { id: 3, keyword: 'Limited', occurrences: 33 },
    { id: 4, keyword: 'Black', occurrences: 26 },
    { id: 5, keyword: 'Case', occurrences: 24 },
    { id: 6, keyword: 'Dell', occurrences: 56 },
    { id: 7, keyword: 'Corporate', occurrences: 45 },
    { id: 8, keyword: 'Limited', occurrences: 33 },
    { id: 9, keyword: 'Black', occurrences: 26 },
    { id: 10, keyword: 'Case', occurrences: 24 },
];

const KeywordAnalysisModal: React.FC<KeywordAnalysisModalProps> = ({
    visible,
    onClose,
    onCreateRule,
    onResolve,
}) => {
    const [selectedKeyword, setSelectedKeyword] = useState<Keyword | null>(
        null
    );
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    const handleClose = () => {
        setSelectedKeyword(null);
        setSelectedIndex(null);
        onClose();
    };

    const handleRowClick = (data: Keyword) => {
        if (selectedKeyword?.id === data.id) {
            setSelectedKeyword(null);
            setSelectedIndex(null);
        } else {
            setSelectedKeyword(data);
            setSelectedIndex(data.id);
        }
    };

    const handleCreateRule = () => {
        if (!selectedKeyword) return;
        onCreateRule(selectedKeyword);
        setSelectedKeyword(null);
        setSelectedIndex(null);
    };

    const keywordColumns: ColumnDef<Keyword, any>[] = useMemo(
        () => [
            { accessorKey: 'keyword', header: 'Keyword', enableSorting: true },
            {
                accessorKey: 'occurrences',
                header: 'Occurrences',
                enableSorting: true,
            },
        ],
        []
    );

    const selectedIndexes = selectedIndex !== null ? [selectedIndex] : [];

    return (
        <Modal
            visible={visible}
            title="Most Recurring Keywords"
            onCancel={handleClose}
            modalStyle={{ width: '500px' }}
            footer={
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        kind="primary"
                        onClick={handleCreateRule}
                        disabled={!selectedKeyword}
                    >
                        Create Rule
                    </Button>
                </div>
            }
        >
            <div className="keyword-analysis">
                <div className="keyword-analysis__resolve-banner">
                    <div className="keyword-analysis__resolve-icon">
                        <span className="Appkit4-icon icon-alert-outline" />
                    </div>
                    <div className="keyword-analysis__resolve-content">
                        <span className="keyword-analysis__resolve-title">
                            Resolve Conflicts
                        </span>
                        <span className="keyword-analysis__resolve-description">
                            Please make sure you run rules first before you
                            commit a new rule.
                        </span>
                        <span
                            className="keyword-analysis__resolve-link"
                            onClick={onResolve}
                        >
                            Resolve
                        </span>
                    </div>
                </div>

                <div className="keyword-analysis__table">
                    <DataTable
                        data={keywordsData}
                        columns={keywordColumns}
                        sorting
                        condensed
                        onRowClick={handleRowClick}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default KeywordAnalysisModal;
export type { Keyword };
